import { useEffect, useState } from "react";
import { ApiError } from "@plotlounge/shared/api";
import styles from "./NudgeDialog.module.scss";

export type NudgeMode = "single" | "batch";

const MAX_MESSAGE_LENGTH = 500;

export interface NudgeDialogProps {
  mode: NudgeMode;
  members: Array<{ id: string; displayName: string }>;
  remaining: number;
  isCapped: boolean;
  isSending: boolean;
  error: Error | null;
  onSubmit: (message: string) => void;
  onClose: () => void;
}

export function NudgeDialog({
  mode,
  members,
  remaining,
  isCapped,
  isSending,
  error,
  onSubmit,
  onClose,
}: NudgeDialogProps) {
  const [message, setMessage] = useState("");

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const recipientCount = members.length;
  const overCap = isCapped || recipientCount > remaining;
  const apiError =
    error instanceof ApiError ? error.message : (error?.message ?? null);

  const labelTitle =
    mode === "batch"
      ? `Nudge ${recipientCount} member${recipientCount === 1 ? "" : "s"}`
      : `Nudge ${members[0]?.displayName ?? "member"}`;

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={labelTitle}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.dialog} data-testid="nudge-dialog">
        <header className={styles.head}>
          <p className={styles.eyebrow}>
            {mode === "batch" ? "Batch nudge" : "Single nudge"}
          </p>
          <h2 className={styles.title}>{labelTitle}</h2>
        </header>

        <div className={styles.body}>
          {mode === "batch" && (
            <div className={styles.recipients} aria-label="Recipients">
              {members.map((m) => (
                <span key={m.id} className={styles.chip}>
                  {m.displayName}
                </span>
              ))}
            </div>
          )}

          <label>
            <span className={styles.fieldLabel}>Message (optional)</span>
            <textarea
              className={styles.textarea}
              value={message}
              onChange={(e) =>
                setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))
              }
              placeholder={
                mode === "batch"
                  ? "Same message goes to everyone you selected."
                  : "Make it personal — they'll see this in their push."
              }
              maxLength={MAX_MESSAGE_LENGTH}
              autoFocus
            />
            <div className={styles.charHint}>
              {message.length}/{MAX_MESSAGE_LENGTH}
            </div>
          </label>

          {overCap && (
            <div className={styles.warn} role="status">
              {isCapped
                ? "You've hit the daily nudge cap. Try again tomorrow."
                : `Selecting ${recipientCount} but only ${remaining} nudges left in your daily quota.`}
            </div>
          )}

          {apiError && !overCap && (
            <div className={styles.errorBlock} role="alert">
              {apiError}
            </div>
          )}
        </div>

        <footer className={styles.foot}>
          <button
            type="button"
            className={styles.btn}
            onClick={onClose}
            disabled={isSending}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={isSending || overCap}
            onClick={() => onSubmit(message.trim())}
          >
            {isSending
              ? "Sending…"
              : mode === "batch"
                ? `Send to ${recipientCount}`
                : "Send nudge"}
          </button>
        </footer>
      </div>
    </div>
  );
}
